import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIdentity } from './IdentityContext';
import { useConfirm } from './ConfirmContext';
import { useToast } from './ToastContext';
import Spinner from './Spinner';
import { findImageByRole } from '../data/imageUrl';
import { GOLD, GOLD_LT } from '../theme';
import PublishBar from './PublishBar';
import StatusBadge from './StatusBadge';
import { publishStatus } from './publishStatus';
import useAuthedImage from './useAuthedImage';

function CardThumb({ image }) {
  const src = useAuthedImage(image);
  return (
    <div style={{ position: 'relative', width: '100%', aspectRatio: '3 / 4', background: '#231a11', marginBottom: 12, overflow: 'hidden' }}>
      {src && <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
    </div>
  );
}

export default function CollectionsListView() {
  const { authFetch } = useIdentity();
  const confirm = useConfirm();
  const toast = useToast();
  const [doc, setDoc] = useState(null);
  const [publishedDoc, setPublishedDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingSlug, setDeletingSlug] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return Promise.all([
      authFetch('/.netlify/functions/collections-draft').then((r) => {
        if (!r.ok) throw new Error('Failed to load draft collections');
        return r.json();
      }),
      fetch('/.netlify/functions/collections-public').then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ])
      .then(([draft, published]) => {
        setDoc(draft);
        setPublishedDoc(published);
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [authFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (slug) => {
    const ok = await confirm(`Delete "${slug}" from the draft? This cannot be undone.`);
    if (!ok) return;
    setDeletingSlug(slug);
    try {
      const res = await authFetch(`/.netlify/functions/collections-draft?slug=${encodeURIComponent(slug)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success(`"${slug}" deleted from the draft.`);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to delete.');
    } finally {
      setDeletingSlug(null);
    }
  };

  if (loading) return <p style={{ color: 'rgba(239,228,210,.6)' }}>Loading…</p>;
  if (error) return <p style={{ color: '#e08a8a' }}>Couldn't load collections. Try reloading the page.</p>;

  const collections = (doc && doc.collections) || [];
  const publishedCollections = publishedDoc ? publishedDoc.collections : null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
        <h1 style={{ fontFamily: "'Bodoni Moda', serif", fontStyle: 'italic', fontWeight: 500, fontSize: 'clamp(28px,3.4vw,40px)', margin: 0 }}>Collections</h1>
        <Link to="/admin/collections/new" style={{ fontWeight: 600, fontSize: 13, letterSpacing: '.18em', textTransform: 'uppercase', padding: '12px 24px', background: GOLD, color: '#16110c', textDecoration: 'none' }}>+ Add Collection</Link>
      </div>

      {collections.length === 0 && <p style={{ color: 'rgba(239,228,210,.55)' }}>No collections yet — add your first one.</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 20, marginBottom: 40 }}>
        {collections.map((c) => {
          const thumb = findImageByRole(c, 'thumb');
          const count = (c.images || []).length;
          const status = publishStatus(c, publishedCollections);
          return (
            <div key={c.slug} style={{ border: '1px solid rgba(201,162,75,.3)', padding: 16 }}>
              {thumb ? <CardThumb image={thumb} /> : <div style={{ width: '100%', aspectRatio: '3 / 4', background: '#231a11', marginBottom: 12 }} />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, margin: '0 0 4px' }}>
                <h3 style={{ fontFamily: "'Bodoni Moda', serif", margin: 0, fontSize: 20 }}>{c.name}</h3>
                <StatusBadge status={status} />
              </div>
              <div style={{ fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: GOLD_LT, marginBottom: 4 }}>{c.tagline}</div>
              <div style={{ fontSize: 12, color: 'rgba(239,228,210,.5)', marginBottom: 12 }}>{count} image{count === 1 ? '' : 's'}</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <Link to={`/admin/collections/${c.slug}`} style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: GOLD_LT, textDecoration: 'none' }}>Edit</Link>
                <button onClick={() => handleDelete(c.slug)} disabled={deletingSlug === c.slug} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: '#e08a8a', background: 'none', border: 'none', cursor: deletingSlug === c.slug ? 'default' : 'pointer', opacity: deletingSlug === c.slug ? .6 : 1, padding: 0 }}>
                  {deletingSlug === c.slug && <Spinner size={11} />}
                  {deletingSlug === c.slug ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <PublishBar />
    </div>
  );
}
